import { useState, useMemo, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { PermissionGate } from "@/components/PermissionGate";
import { useVendorPO, type Vendor, type OrderingMethod } from "@/data/VendorPOContext";
import { VendorPOService } from "@/services/vendor.service";
import { useRole } from "@/data/RoleContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Pencil, Truck, FileText, CheckCircle2, Clock, Package, AlertTriangle } from "lucide-react";
import { useActionPermissions } from "@/hooks/useActionPermissions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitFilterBar, CockpitSearch,
} from "@/components/cockpit/CockpitShell";

const EMPTY_VENDOR: Omit<Vendor, "vendorId"> = {
  vendorName: "", contactName: "", phone: "", email: "",
  orderingMethod: "Email", notes: "", active: true,
};

const METHOD_COLORS: Record<OrderingMethod, string> = {
  Email: "bg-info/15 text-info border-info/30",
  Portal: "bg-primary/15 text-primary border-primary/30",
  Manual: "bg-warning/15 text-warning border-warning/30",
};

export default function VendorsPage() {
  const { vendors, setVendors, purchaseOrders, getPOsByVendor } = useVendorPO();
  const { role } = useRole();
  const actionPerms = useActionPermissions();
  const canEdit = actionPerms.can("edit_vendor_records");

  const [tab, setTab] = useState("vendors");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);
  const [draft, setDraft] = useState<Omit<Vendor, "vendorId">>(EMPTY_VENDOR);

  const filteredVendors = useMemo(() =>
    vendors.filter((v) =>
      !search || v.vendorName.toLowerCase().includes(search.toLowerCase()) || v.contactName.toLowerCase().includes(search.toLowerCase())
    ), [vendors, search]);

  const filteredPOs = useMemo(() => {
    if (!search) return purchaseOrders;
    const s = search.toLowerCase();
    return purchaseOrders.filter((po) =>
      po.poNumber.toLowerCase().includes(s) || po.vendorName.toLowerCase().includes(s) || po.jobLabel.toLowerCase().includes(s)
    );
  }, [purchaseOrders, search]);
  const vendorsPagination = usePagination(filteredVendors, 25);
  const poPagination = usePagination(filteredPOs, 25);
  useEffect(() => {
    vendorsPagination.reset();
    poPagination.reset();
  }, [search, tab]);

  const handleSave = async () => {
    if (!draft.vendorName) { toast.error("Vendor name is required"); return; }
    try {
      if (editVendor) {
        const updated = await VendorPOService.updateVendor(editVendor.vendorId, {
          name: draft.vendorName,
          contact_name: draft.contactName || undefined,
          contact_email: draft.email || undefined,
          contact_phone: draft.phone || undefined,
          ordering_method: draft.orderingMethod,
          notes: draft.notes || undefined,
          is_active: draft.active,
        });
        setVendors((prev) => prev.map((v) => v.vendorId === editVendor.vendorId ? updated : v));
        toast.success("Vendor updated");
      } else {
        const code = draft.vendorName
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")
          .slice(0, 24) || "VENDOR";
        const created = await VendorPOService.createVendor({
          name: draft.vendorName,
          code,
          contact_name: draft.contactName || undefined,
          contact_email: draft.email || undefined,
          contact_phone: draft.phone || undefined,
          ordering_method: draft.orderingMethod,
          notes: draft.notes || undefined,
          is_active: draft.active,
        });
        setVendors((prev) => [created, ...prev]);
        toast.success("Vendor added");
      }
      setEditVendor(null); setShowAdd(false); setDraft(EMPTY_VENDOR);
    } catch (error) {
      console.error("[VendorsPage] Failed to save vendor:", error);
      toast.error("Failed to save vendor");
    }
  };

  const openEdit = (v: Vendor) => { setEditVendor(v); setDraft({ ...v }); setShowAdd(true); };

  const statusIcon = (status: string) => {
    switch (status) {
      case "Draft": return <Clock className="h-3 w-3 text-muted-foreground" />;
      case "Submitted": return <Package className="h-3 w-3 text-info" />;
      case "Confirmed": return <CheckCircle2 className="h-3 w-3 text-success" />;
      case "Delivered": return <Truck className="h-3 w-3 text-success" />;
      default: return null;
    }
  };

  return (
    <CockpitPage>
      <CockpitMasthead
        // eyebrow="Catalog · Vendors"
        title="Vendors & Purchase Orders"
        right={
          <div className="flex items-center gap-4">
            <HeroMetric label="Active" value={vendors.filter(v => v.active).length} />
            <HeroDivider />
            <HeroMetric label="Total POs" value={purchaseOrders.length} />
            <HeroDivider />
            <HeroMetric
              label="Awaiting"
              value={purchaseOrders.filter(po => po.status === "Submitted").length}
              tone="warn"
            />
            <HeroDivider />
            <HeroMetric
              label="Flagged"
              value={purchaseOrders.filter(po => po.flagged).length}
              tone={purchaseOrders.filter(po => po.flagged).length > 0 ? "bad" : "default"}
            />
            {canEdit && tab === "vendors" && (
              <>
                <HeroDivider />
                <Button size="sm" onClick={() => { setEditVendor(null); setDraft(EMPTY_VENDOR); setShowAdd(true); }}>
                  <Plus className="h-3 w-3 mr-1" /> Add Vendor
                </Button>
              </>
            )}
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <CockpitFilterBar
          search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search vendors or POs…" />}
          right={
            <TabsList className="h-8">
              <TabsTrigger value="vendors" className="text-xs">Vendors</TabsTrigger>
              <TabsTrigger value="purchase-orders" className="text-xs">Purchase Orders</TabsTrigger>
            </TabsList>
          }
        />

        <TabsContent value="vendors">
          <Card>
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Vendor Name</TableHead>
                    <TableHead className="text-[10px]">Contact</TableHead>
                    <TableHead className="text-[10px]">Phone</TableHead>
                    <TableHead className="text-[10px]">Email</TableHead>
                    <TableHead className="text-[10px]">Ordering Method</TableHead>
                    <TableHead className="text-[10px] text-right">Active POs</TableHead>
                    <TableHead className="text-[10px]">Status</TableHead>
                    {canEdit && <TableHead className="text-[10px]" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorsPagination.paged.map((v) => {
                    const activePOs = getPOsByVendor(v.vendorId).filter((po) => po.status !== "Delivered").length;
                    return (
                      <TableRow key={v.vendorId} className={!v.active ? "opacity-50" : ""}>
                        <TableCell className="text-xs font-medium">{v.vendorName}</TableCell>
                        <TableCell className="text-xs">{v.contactName}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{v.phone}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{v.email}</TableCell>
                        <TableCell><Badge className={`text-[9px] ${METHOD_COLORS[v.orderingMethod]}`}>{v.orderingMethod}</Badge></TableCell>
                        <TableCell className="text-xs text-right font-mono">{activePOs}</TableCell>
                        <TableCell>
                          {v.active
                            ? <Badge className="text-[9px] bg-success/15 text-success border-success/30">Active</Badge>
                            : <Badge variant="secondary" className="text-[9px]">Inactive</Badge>}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(v)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
          <PaginationBar {...vendorsPagination} />
        </TabsContent>

        <TabsContent value="purchase-orders">
          <Card>
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">PO #</TableHead>
                    <TableHead className="text-[10px]">Job</TableHead>
                    <TableHead className="text-[10px]">Vendor</TableHead>
                    <TableHead className="text-[10px] text-right">Items</TableHead>
                    <TableHead className="text-[10px] text-right">Total</TableHead>
                    <TableHead className="text-[10px]">Status</TableHead>
                    <TableHead className="text-[10px]">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poPagination.paged.map((po) => (
                    <TableRow key={po.poId} className={po.flagged ? "bg-destructive/5" : ""}>
                      <TableCell className="text-xs font-mono font-medium">{po.poNumber}</TableCell>
                      <TableCell className="text-xs">{po.jobLabel}</TableCell>
                      <TableCell className="text-xs">{po.vendorName}</TableCell>
                      <TableCell className="text-xs text-right">{po.items.length}</TableCell>
                      <TableCell className="text-xs text-right font-mono">${po.totalCost.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px] gap-1">
                          {statusIcon(po.status)} {po.status}
                        </Badge>
                        {po.flagged && <Badge variant="destructive" className="text-[9px] ml-1"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" />Flagged</Badge>}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">{new Date(po.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {filteredPOs.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-8">No purchase orders</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
          <PaginationBar {...poPagination} />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setEditVendor(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editVendor ? "Edit Vendor" : "Add Vendor"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label className="text-xs">Vendor Name *</Label><Input className="h-8 text-xs" value={draft.vendorName} onChange={(e) => setDraft({ ...draft, vendorName: e.target.value })} /></div>
            <div><Label className="text-xs">Contact Name</Label><Input className="h-8 text-xs" value={draft.contactName} onChange={(e) => setDraft({ ...draft, contactName: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input className="h-8 text-xs" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label><Input className="h-8 text-xs" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ordering Method</Label>
                <Select value={draft.orderingMethod} onValueChange={(v) => setDraft({ ...draft, orderingMethod: v as OrderingMethod })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Portal">Portal</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <Switch checked={draft.active} onCheckedChange={(c) => setDraft({ ...draft, active: c })} />
                <Label className="text-xs">{draft.active ? "Active" : "Inactive"}</Label>
              </div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea className="text-xs" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditVendor(null); }}>Cancel</Button>
            <PermissionGate permission="ordering_create">
              <Button onClick={handleSave}><CheckCircle2 className="h-3 w-3 mr-1" /> {editVendor ? "Save Changes" : "Add Vendor"}</Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CockpitPage>
  );
}
